"use client";

/**
 * RFLP per-type tool-call result cards. Registered (side-effect) into the
 * generic chat-shell registry — keeps `@sourcepilot/ui` domain-agnostic while
 * the app supplies the RFLP-specific layouts. Imported once at app load from
 * SourcePilotAppHostProvider.
 *
 * The data comes from the response's `card_summary` (`{context, rows}`),
 * resolved + cached server-side on the ToolCallResponse at DAG egress (keyed
 * by a structural hash of the card logic). The card just renders it — no
 * graph-store resolution, no parsing of composed strings.
 *
 * AttributeValue (header stays just the tool name; everything below it):
 *   line 1 — "{owner}"                       (summary.context)
 *   line 2 — "{attribute} = {value} {unit}"  (summary.rows)
 */

import React from "react";

import {
  registerEntityResultCard,
  type EntityResultCardContext,
} from "@sourcepilot/ui/shell/chat/entity-result-cards";

interface SummaryRow {
  label: string;
  value: string;
}

function AttributeValueBody({ summary }: EntityResultCardContext) {
  const ctx: string | null = summary?.context ?? null;
  const rows: SummaryRow[] = Array.isArray(summary?.rows) ? summary.rows : [];
  if (!ctx && rows.length === 0) return null;
  return (
    <div className="mt-2 space-y-1">
      {ctx ? (
        <div className="text-sm font-medium text-gray-800">{ctx}</div>
      ) : null}
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2 text-sm">
          <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-700">
            {r.label}
          </span>
          <span className="text-gray-400">=</span>
          <span className="font-semibold text-gray-900">{r.value}</span>
        </div>
      ))}
    </div>
  );
}

registerEntityResultCard("AttributeValue", {
  // No header subtitle — the part name renders on its own line in the body.
  Body: AttributeValueBody,
});
